// Moteur des promotions (lot 2), ligne par ligne : quelles lignes d'une réservation une promotion remise,
// de combien, et laquelle retenir quand plusieurs s'appliquent (spec Promotions § 5, plan
// docs/to_implement/promotions-parcours-etape-3-plan.md § 3.2).
//
// Fonctions PURES, en centimes : un seul calcul pour le récap du tunnel, la pré-réservation, la pose d'un
// code, l'aperçu de l'éditeur et l'agenda. La résolution d'un code (introuvable, code personnel d'un autre
// client, déjà utilisé) et le chargement des règles restent côté backend.
//
// Une promotion :
//   - remise les lignes de son périmètre (prestations, lieux, membres) dont la formule a un prix (FIXED,
//     FROM), quel que soit le mode de paiement : une ligne réglée sur place est remisée aussi ;
//   - juge chaque ligne sur son propre créneau (happy hours, et dates quand elles bornent le rendez-vous)
//     et sur ses propres membres (lot 4) ;
//   - base d'une ligne = prix de la formule × unités, suppléments compris seulement si l'option est
//     cochée, moins ce qu'en couvre déjà un bon PACK ou PRESTATION (sans l'option, seule la part de
//     formule couverte en sort, donnée par l'appelant avec la couverture : les suppléments couverts ne
//     rognent pas la formule des autres unités) ;
//   - pourcentage arrondi au centime, montant fixe = min(valeur, base) PAR LIGNE ;
//   - une seule par réservation : la plus avantageuse des automatiques applicables et du code saisi, le
//     code ne l'emportant que s'il donne au moins autant.

import { computeBookingLineGrossMinor, type BookingPricingLineInput } from './booking-pricing';
import { PriceType } from './translation_namespace/price_namespace';
import { formatPriceFromMinorUnit } from './translation_namespace/utils';
import {
  BookingPromoIneligibilityReason,
  ProPromoCodeCustomerScope,
  ProPromoCodeKind,
  ProPromoCodeStatus,
  ProPromoCodeTrigger,
  ProPromoCodeWindowBasis,
} from './translation_namespace/pro_promo_code_namespace';

export const PROMO_PERCENT_VALUE_MAX = 100;

/**
 * Valide la cohérence `value` ↔ `kind` (PERCENT = 1-100, FIXED = centimes > 0). Une remise nulle est
 * refusée : la promotion ne donnerait jamais rien (ZERO_DISCOUNT à chaque réservation). Retourne `null`
 * si OK, sinon un message d'erreur (validation métier, `value` étant partagé entre les deux kinds).
 */
export function validateProPromoValueForKind(kind: ProPromoCodeKind, value: number): string | null {
  if (!Number.isInteger(value) || value <= 0) {
    return 'value must be strictly positive';
  }
  if (kind === ProPromoCodeKind.PERCENT && value > PROMO_PERCENT_VALUE_MAX) {
    return `PERCENT value must be between 1 and ${PROMO_PERCENT_VALUE_MAX}`;
  }
  return null;
}

// ─── Happy hours ───────────────────────────────────────────────────────────

/** Happy hours d'une promotion : jours ISO 1-7 ([] = tous les jours), plages HH:MM ([] = toute la journée). */
export interface PromotionHappyHours {
  readonly days_of_week: readonly number[];
  readonly time_windows: readonly { start: string; end: string }[];
}

const ISO_WEEKDAY: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };
const localTimeFormats = new Map<string, Intl.DateTimeFormat>();

/** Jour ISO (1-7) et heure « HH:MM » de `instant` dans le fuseau du pro. */
function toLocalWeekdayAndTime(instant: Date, timeZone: string): { weekday: number; hhmm: string } {
  let format = localTimeFormats.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    localTimeFormats.set(timeZone, format);
  }
  const parts = format.formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((candidate): boolean => candidate.type === type)?.value ?? '';
  return { weekday: ISO_WEEKDAY[part('weekday')] ?? 0, hhmm: `${part('hour')}:${part('minute')}` };
}

/** `true` si l'heure locale `hhmm` (HH:MM) tombe dans une des plages (gère les plages à cheval sur minuit). */
function isWithinTimeWindows(hhmm: string, windows: PromotionHappyHours['time_windows']): boolean {
  if (windows.length === 0) return true;
  return windows.some(({ start, end }): boolean => {
    if (start <= end) return hhmm >= start && hhmm <= end;
    return hhmm >= start || hhmm <= end;
  });
}

/** La promotion a-t-elle des happy hours (jours ou plages horaires) ? */
export function hasPromoHappyHours(happyHours: PromotionHappyHours): boolean {
  return happyHours.days_of_week.length > 0 || happyHours.time_windows.length > 0;
}

/**
 * Le début du créneau tombe-t-il dans les happy hours (jours ISO + plages HH:MM, en heure locale du pro) ?
 * Toujours `true` sans contrainte. Source unique du verdict : évaluation au panier ET déplacement d'une
 * réservation déjà remisée (le client ne peut la déplacer que vers un créneau où la promotion vaut encore).
 */
export function isWithinPromoHappyHours(
  slotStart: Date,
  proTimezone: string,
  happyHours: PromotionHappyHours,
): boolean {
  if (!hasPromoHappyHours(happyHours)) return true;
  const { weekday, hhmm } = toLocalWeekdayAndTime(slotStart, proTimezone);
  const dayOk = happyHours.days_of_week.length === 0 || happyHours.days_of_week.includes(weekday);
  return dayOk && isWithinTimeWindows(hhmm, happyHours.time_windows);
}

// ─── Entrées ───────────────────────────────────────────────────────────────

/** Ligne de la réservation vue par une promotion. */
export interface PromotionLineInput
  extends Pick<BookingPricingLineInput, 'prestation_id' | 'unit_price_minor' | 'supplements' | 'billable_units'> {
  /** Type de prix de la formule retenue : seules FIXED et FROM sont remisées. */
  price_type: PriceType | string;
  /** Lieu du rendez-vous de la ligne ; null : pas encore choisi (récap avant le créneau). */
  pro_place_id: number | null;
  /**
   * Début du créneau de la ligne (UTC) ; null : pas encore choisi. Absent : le créneau de la réservation
   * (`timing.slot_start`), pour un appelant qui n'en connaît qu'un.
   */
  slot_start?: Date | null;
  /**
   * Membres affectés à la ligne. Absent ou null : pas encore connus (« Sans préférence » avant la
   * réservation) ; vide : aucun membre, et c'est acquis (prestation en libre-service, rendez-vous de
   * l'agenda sans membre) — une promotion limitée à des membres ne la remise pas.
   */
  team_member_ids?: readonly number[] | null;
}

/** Règles d'une promotion, chargées par l'appelant (catégories déjà résolues vers leurs prestations). */
export interface PromotionRules {
  promotion_id: number;
  trigger: ProPromoCodeTrigger;
  status: ProPromoCodeStatus;
  kind: ProPromoCodeKind;
  value: number;
  applies_to_supplements: boolean;
  /** Prestations du périmètre ; null : tout le catalogue. */
  target_prestation_ids: ReadonlySet<number> | null;
  /** Lieux du périmètre ; null ou vide : tous les lieux. */
  place_ids: ReadonlySet<number> | null;
  /** Membres concernés (option « Restreindre à certains membres ») ; null ou vide : tous les membres. */
  team_member_ids: ReadonlySet<number> | null;
  customer_scope: ProPromoCodeCustomerScope;
  /** Tags des clients concernés (`customer_scope` = TAGS) : au moins un. */
  tag_ids: ReadonlySet<number>;
  /** Montant minimum, sur le brut des lignes remisées. */
  min_cart_amount_minor: number | null;
  valid_from: Date;
  /** Dernière milliseconde du dernier jour chez le pro (`promoValidUntilInstant`), bornes incluses. */
  valid_until: Date | null;
  /** Ce que bornent les dates : le début du rendez-vous de chaque ligne, ou l'instant de la réservation. */
  window_basis: ProPromoCodeWindowBasis;
  /** Jours ISO 1-7 ([] = tous les jours). */
  days_of_week: readonly number[];
  /** Plages HH:MM en heure locale du pro ([] = toute la journée). */
  time_windows: readonly { start: string; end: string }[];
  max_redemptions: number | null;
  /** Utilisations actives, hors celle que la réservation évaluée tient déjà. */
  current_redemptions: number;
  max_redemptions_per_customer: number | null;
}

/** Client de la réservation ; null tant qu'il n'est pas identifié (récap avant le formulaire). */
export interface PromotionCustomer {
  /** Aucune réservation confirmée ni en attente de validation chez le pro (un client sans fiche l'est). */
  is_first_time: boolean;
  tag_ids: ReadonlySet<number>;
  /** Utilisations actives du client, par promotion, hors la réservation évaluée. */
  active_redemptions_by_promotion: ReadonlyMap<number, number>;
}

export interface PromotionTiming {
  /** Début du premier créneau (UTC) ; null tant qu'il n'est pas choisi. Repli des lignes sans créneau propre. */
  slot_start: Date | null;
  pro_timezone: string;
  /** Instant d'évaluation (fenêtre de validité). */
  now: Date;
}

export interface PromotionContext {
  lines: readonly PromotionLineInput[];
  /** Part de chaque ligne couverte par un bon PACK ou PRESTATION (`computeVoucherLineCoverageParts().total_minor`). */
  line_coverage_minor?: readonly number[];
  /**
   * Part de `line_coverage_minor` qui porte sur la formule, hors suppléments
   * (`computeVoucherLineCoverageParts().formula_minor`) : ce qui sort de la base d'une promotion sans
   * l'option « suppléments inclus ». Absente (ou ligne sans valeur) : la couverture est imputée d'abord
   * sur la formule, `min(couverture, formule × unités)`.
   */
  line_coverage_formula_minor?: readonly number[];
  timing: PromotionTiming;
  customer: PromotionCustomer | null;
}

// ─── Évaluation d'une promotion ────────────────────────────────────────────

/** Condition d'une promotion appliquée qui ne peut pas encore être tranchée. */
export enum PromotionPendingCondition {
  /** Happy hours, créneau pas encore choisi. */
  HAPPY_HOURS = 'HAPPY_HOURS',
  /** Lieux limités, lieu pas encore choisi. */
  PLACE = 'PLACE',
  /** Nouveaux clients ou tags, client pas encore identifié. */
  CUSTOMER = 'CUSTOMER',
  /** Membres limités, membre pas encore affecté (« Sans préférence »). */
  MEMBER = 'MEMBER',
  /** Dates du rendez-vous à venir, créneau pas encore choisi. */
  DATES = 'DATES',
}

export interface AppliedPromotion {
  eligible: true;
  promotion_id: number;
  trigger: ProPromoCodeTrigger;
  /** Remise de chaque ligne, dans l'ordre des lignes. */
  line_discounts_minor: number[];
  discount_minor: number;
  /** Vide : remise acquise ; sinon remise annoncée, tranchée plus tard dans le tunnel. */
  pending_conditions: PromotionPendingCondition[];
}

export interface RejectedPromotion {
  eligible: false;
  promotion_id: number;
  trigger: ProPromoCodeTrigger;
  reason: BookingPromoIneligibilityReason;
}

export type PromotionEvaluation = AppliedPromotion | RejectedPromotion;

const DISCOUNTED_PRICE_TYPES: ReadonlySet<string> = new Set([PriceType.FIXED, PriceType.FROM]);

/** La promotion vise-t-elle des lieux en particulier ? */
const restrictsPlaces = (rules: Pick<PromotionRules, 'place_ids'>): boolean =>
  rules.place_ids !== null && rules.place_ids.size > 0;

/** La promotion vise-t-elle des membres en particulier ? */
const restrictsMembers = (rules: Pick<PromotionRules, 'team_member_ids'>): boolean =>
  rules.team_member_ids !== null && rules.team_member_ids.size > 0;

/** Instant dans la fenêtre de validité (bornes incluses). */
const isWithinValidity = (rules: Pick<PromotionRules, 'valid_from' | 'valid_until'>, instant: Date): boolean =>
  instant >= rules.valid_from && (rules.valid_until === null || instant <= rules.valid_until);

/** Créneau d'une ligne : le sien, sinon celui de la réservation. */
const lineSlotStart = (line: PromotionLineInput, timing: PromotionTiming): Date | null =>
  line.slot_start !== undefined ? line.slot_start : timing.slot_start;

/** Verdict d'une ligne sur une condition de créneau ou de membre : oui, non, ou pas encore connu. */
type LineVerdict = 'yes' | 'no' | 'unknown';

/** Prix d'une ligne de promotion : formule et suppléments réservés, unités facturées. */
type PromotionPricedLine = Pick<PromotionLineInput, 'unit_price_minor' | 'supplements' | 'billable_units'>;

/**
 * Part de la formule (hors suppléments) que couvre un bon sur la ligne : celle que donne l'appelant avec la
 * couverture (`line_coverage_formula_minor`, calculée par `computeVoucherLineCoverageParts` : une personne
 * couverte, huile comprise, ne couvre qu'une formule), sinon la couverture imputée d'abord sur la formule.
 * Jamais plus que la couverture, ni que la formule × unités.
 */
function resolveCoveredFormulaMinor(
  line: PromotionPricedLine,
  coverageMinor: number,
  coverageFormulaMinor: number | undefined,
): number {
  const coveredMinor = Math.max(0, coverageMinor);
  const coveredFormulaMinor = coverageFormulaMinor === undefined ? coveredMinor : Math.max(0, coverageFormulaMinor);
  return Math.min(coveredFormulaMinor, coveredMinor, Math.max(0, line.unit_price_minor * line.billable_units));
}

/**
 * Base remisable d'une ligne, diminuée de ce que couvre un bon de prestation : formule × unités moins la
 * part de formule couverte (`resolveCoveredFormulaMinor`) ; suppléments compris si l'option est cochée,
 * et alors toute la couverture (suppléments compris) en est retirée. Une formule payée par un bon n'est
 * pas remisée ; ses suppléments payés par le client le restent si l'option est cochée.
 */
function computePromotionLineBaseMinor(
  rules: Pick<PromotionRules, 'applies_to_supplements'>,
  line: PromotionPricedLine,
  coverageMinor = 0,
  coverageFormulaMinor?: number,
): number {
  if (rules.applies_to_supplements) {
    return Math.max(0, computeBookingLineGrossMinor(line) - Math.max(0, coverageMinor));
  }
  return Math.max(
    0,
    line.unit_price_minor * line.billable_units - resolveCoveredFormulaMinor(line, coverageMinor, coverageFormulaMinor),
  );
}

/**
 * Remise d'une ligne du périmètre : sur sa base après bon (cf. `computePromotionLineBaseMinor`,
 * `coverageMinor` = ce que le bon couvre de la ligne, `coverageFormulaMinor` = sa part de formule). Ne
 * dépasse jamais la part de la ligne que le bon ne couvre pas.
 */
export function computePromotionLineDiscountMinor(
  rules: Pick<PromotionRules, 'kind' | 'value' | 'applies_to_supplements'>,
  line: PromotionPricedLine,
  coverageMinor = 0,
  coverageFormulaMinor?: number,
): number {
  const baseMinor = computePromotionLineBaseMinor(rules, line, coverageMinor, coverageFormulaMinor);
  if (baseMinor <= 0) return 0;
  const value = Math.max(0, rules.value);
  const discountMinor =
    rules.kind === ProPromoCodeKind.PERCENT ? Math.round((baseMinor * value) / 100) : Math.min(value, baseMinor);
  return Math.max(0, Math.min(discountMinor, baseMinor));
}

/** Happy hours d'une ligne, sur son créneau. */
function happyHoursVerdict(rules: PromotionRules, line: PromotionLineInput, timing: PromotionTiming): LineVerdict {
  if (!hasPromoHappyHours(rules)) return 'yes';
  const slotStart = lineSlotStart(line, timing);
  if (slotStart === null) return 'unknown';
  return isWithinPromoHappyHours(slotStart, timing.pro_timezone, rules) ? 'yes' : 'no';
}

/**
 * Dates d'une ligne quand elles bornent le rendez-vous. Créneau inconnu : acquis si la fenêtre est déjà
 * ouverte (le client choisira très probablement un créneau proche, et chaque créneau dit s'il est remisé),
 * en attente si elle n'a pas commencé.
 */
function datesVerdict(rules: PromotionRules, line: PromotionLineInput, timing: PromotionTiming): LineVerdict {
  if (rules.window_basis !== ProPromoCodeWindowBasis.APPOINTMENT_AT) return 'yes';
  const slotStart = lineSlotStart(line, timing);
  if (slotStart === null) return timing.now >= rules.valid_from ? 'yes' : 'unknown';
  return isWithinValidity(rules, slotStart) ? 'yes' : 'no';
}

/**
 * Membre d'une ligne : un des membres affectés est dans la liste. Membres pas encore connus (absents ou
 * null) : en attente ; aucun membre (liste vide, libre-service) : la remise suit le membre affecté, sans
 * membre elle ne s'applique pas.
 */
function memberVerdict(rules: PromotionRules, line: PromotionLineInput): LineVerdict {
  if (!restrictsMembers(rules)) return 'yes';
  const memberIds = line.team_member_ids;
  if (memberIds === undefined || memberIds === null) return 'unknown';
  return memberIds.some((memberId): boolean => rules.team_member_ids?.has(memberId) ?? false) ? 'yes' : 'no';
}

/** Refus que vaut chaque condition en attente quand plus rien ne peut la trancher (réservation ferme). */
const PENDING_CONDITION_REJECTION: Record<PromotionPendingCondition, BookingPromoIneligibilityReason> = {
  [PromotionPendingCondition.HAPPY_HOURS]: BookingPromoIneligibilityReason.OUTSIDE_HAPPY_HOURS,
  [PromotionPendingCondition.PLACE]: BookingPromoIneligibilityReason.NOT_APPLICABLE_TO_PLACE,
  [PromotionPendingCondition.CUSTOMER]: BookingPromoIneligibilityReason.CUSTOMER_NOT_TARGETED,
  [PromotionPendingCondition.MEMBER]: BookingPromoIneligibilityReason.NOT_APPLICABLE_TO_MEMBER,
  [PromotionPendingCondition.DATES]: BookingPromoIneligibilityReason.OUTSIDE_VALIDITY_WINDOW,
};

/**
 * Verdict FERME d'une évaluation : à la réservation (pré-réservation, pose d'un code, promotion posée
 * depuis l'agenda), tout est connu, et une condition encore en attente ne peut plus se trancher plus tard —
 * elle ne passe pas pour acquise, la promotion est refusée pour cette condition (la première en attente).
 */
export function settlePromotionEvaluation(evaluation: PromotionEvaluation): PromotionEvaluation {
  if (!evaluation.eligible) return evaluation;
  const [pendingCondition] = evaluation.pending_conditions;
  if (pendingCondition === undefined) return evaluation;
  return {
    eligible: false,
    promotion_id: evaluation.promotion_id,
    trigger: evaluation.trigger,
    reason: PENDING_CONDITION_REJECTION[pendingCondition],
  };
}

/**
 * Évalue une promotion contre la réservation. Raisons dans l'ordre d'affichage : statut, validité, happy
 * hours et dates (quand aucun créneau connu n'y tombe), quota global, client (nouveau, tags, quota par
 * client), périmètre ligne par ligne (prestations, lieux, heures, dates, membres), montant minimum, remise
 * nulle. Une condition qui dépend d'une donnée pas encore connue (créneau, lieu, membre, client) n'est pas
 * refusée : la remise est annoncée avec ses `pending_conditions`.
 */
export function evaluatePromotion(rules: PromotionRules, context: PromotionContext): PromotionEvaluation {
  const reject = (reason: BookingPromoIneligibilityReason): RejectedPromotion => ({
    eligible: false,
    promotion_id: rules.promotion_id,
    trigger: rules.trigger,
    reason,
  });
  const { timing, customer } = context;
  const pending: PromotionPendingCondition[] = [];

  if (rules.status !== ProPromoCodeStatus.ACTIVE) return reject(BookingPromoIneligibilityReason.CODE_INACTIVE);

  // Dates de la réservation : jugées maintenant. Dates du rendez-vous : une promotion terminée ne vaut plus,
  // une promotion à venir vaut pour les rendez-vous de sa fenêtre (jugés ligne par ligne).
  const bookedAtWindow = rules.window_basis === ProPromoCodeWindowBasis.BOOKED_AT;
  if (
    bookedAtWindow ? !isWithinValidity(rules, timing.now) : rules.valid_until !== null && timing.now > rules.valid_until
  ) {
    return reject(BookingPromoIneligibilityReason.OUTSIDE_VALIDITY_WINDOW);
  }

  // Tous les créneaux connus et aucun dans les heures (ou les dates) : refus avant le client, comme quand
  // la réservation n'était jugée que sur son premier créneau.
  const allSlotsKnown = context.lines.every((line): boolean => lineSlotStart(line, timing) !== null);
  if (allSlotsKnown && context.lines.length > 0) {
    if (context.lines.every((line): boolean => happyHoursVerdict(rules, line, timing) === 'no')) {
      return reject(BookingPromoIneligibilityReason.OUTSIDE_HAPPY_HOURS);
    }
    if (context.lines.every((line): boolean => datesVerdict(rules, line, timing) === 'no')) {
      return reject(BookingPromoIneligibilityReason.OUTSIDE_VALIDITY_WINDOW);
    }
  }

  // Quota global : évalué ici, puis réservé sous verrou à la pré-réservation (réserve-ou-échoue).
  if (rules.max_redemptions !== null && rules.current_redemptions >= rules.max_redemptions) {
    return reject(BookingPromoIneligibilityReason.MAX_REDEMPTIONS_REACHED);
  }

  if (customer === null) {
    if (rules.customer_scope !== ProPromoCodeCustomerScope.ALL) pending.push(PromotionPendingCondition.CUSTOMER);
  } else {
    const customerRedemptions = customer.active_redemptions_by_promotion.get(rules.promotion_id) ?? 0;
    // « Nouveaux clients » déjà utilisée par ce client (annulation sans restitution, autre pré-réservation en
    // cours) : il n'est plus nouveau pour elle.
    if (
      rules.customer_scope === ProPromoCodeCustomerScope.NEW &&
      (!customer.is_first_time || customerRedemptions > 0)
    ) {
      return reject(BookingPromoIneligibilityReason.NOT_FIRST_TIME_CUSTOMER);
    }
    if (
      rules.customer_scope === ProPromoCodeCustomerScope.TAGS &&
      ![...rules.tag_ids].some((tagId): boolean => customer.tag_ids.has(tagId))
    ) {
      return reject(BookingPromoIneligibilityReason.CUSTOMER_NOT_TARGETED);
    }
    if (rules.max_redemptions_per_customer !== null && customerRedemptions >= rules.max_redemptions_per_customer) {
      return reject(BookingPromoIneligibilityReason.MAX_PER_CUSTOMER_REACHED);
    }
  }

  // Périmètre, ligne par ligne : prestation et formule chiffrée, puis lieu, heures, dates et membres. Une
  // condition que plus aucune ligne ne remplit donne sa raison ; une condition inconnue reste en attente.
  let eligible = context.lines.map(
    (line): boolean =>
      DISCOUNTED_PRICE_TYPES.has(line.price_type) &&
      (rules.target_prestation_ids === null || rules.target_prestation_ids.has(line.prestation_id)),
  );
  if (!eligible.some(Boolean)) return reject(BookingPromoIneligibilityReason.NOT_APPLICABLE_TO_CART);

  const lineChecks: {
    verdict: (line: PromotionLineInput) => LineVerdict;
    reason: BookingPromoIneligibilityReason;
    condition: PromotionPendingCondition;
  }[] = [
    {
      verdict: (line): LineVerdict =>
        !restrictsPlaces(rules)
          ? 'yes'
          : line.pro_place_id === null
            ? 'unknown'
            : rules.place_ids?.has(line.pro_place_id)
              ? 'yes'
              : 'no',
      reason: BookingPromoIneligibilityReason.NOT_APPLICABLE_TO_PLACE,
      condition: PromotionPendingCondition.PLACE,
    },
    {
      verdict: (line): LineVerdict => happyHoursVerdict(rules, line, timing),
      reason: BookingPromoIneligibilityReason.OUTSIDE_HAPPY_HOURS,
      condition: PromotionPendingCondition.HAPPY_HOURS,
    },
    {
      verdict: (line): LineVerdict => datesVerdict(rules, line, timing),
      reason: BookingPromoIneligibilityReason.OUTSIDE_VALIDITY_WINDOW,
      condition: PromotionPendingCondition.DATES,
    },
    {
      verdict: (line): LineVerdict => memberVerdict(rules, line),
      reason: BookingPromoIneligibilityReason.NOT_APPLICABLE_TO_MEMBER,
      condition: PromotionPendingCondition.MEMBER,
    },
  ];
  for (const check of lineChecks) {
    let unknown = false;
    eligible = context.lines.map((line, index): boolean => {
      if (!eligible[index]) return false;
      const verdict = check.verdict(line);
      if (verdict === 'unknown') unknown = true;
      return verdict !== 'no';
    });
    if (!eligible.some(Boolean)) return reject(check.reason);
    if (unknown) pending.push(check.condition);
  }

  const eligibleGrossMinor = context.lines.reduce(
    (sum, line, index): number => (eligible[index] ? sum + Math.max(0, computeBookingLineGrossMinor(line)) : sum),
    0,
  );
  if (rules.min_cart_amount_minor !== null && eligibleGrossMinor < rules.min_cart_amount_minor) {
    return reject(BookingPromoIneligibilityReason.MIN_CART_AMOUNT_NOT_MET);
  }

  const coverage = context.line_coverage_minor ?? [];
  const formulaCoverage = context.line_coverage_formula_minor;
  const lineDiscountsMinor = context.lines.map((line, index): number =>
    eligible[index]
      ? computePromotionLineDiscountMinor(rules, line, coverage[index] ?? 0, formulaCoverage?.[index])
      : 0,
  );
  const discountMinor = lineDiscountsMinor.reduce((sum, lineDiscount): number => sum + lineDiscount, 0);
  if (discountMinor <= 0) {
    // Tout ce que la promotion viserait est déjà payé par un bon : rien à remiser.
    const hasBase = context.lines.some(
      (line, index): boolean =>
        eligible[index] &&
        computePromotionLineBaseMinor(rules, line, coverage[index] ?? 0, formulaCoverage?.[index]) > 0,
    );
    return reject(
      hasBase ? BookingPromoIneligibilityReason.ZERO_DISCOUNT : BookingPromoIneligibilityReason.NOT_APPLICABLE_TO_CART,
    );
  }

  return {
    eligible: true,
    promotion_id: rules.promotion_id,
    trigger: rules.trigger,
    line_discounts_minor: lineDiscountsMinor,
    discount_minor: discountMinor,
    pending_conditions: pending,
  };
}

// ─── Choix de la promotion ─────────────────────────────────────────────────

/** Meilleure de deux promotions appliquées : la plus forte remise, puis la plus sûre, puis la plus ancienne. */
const isBetterPromotion = (candidate: AppliedPromotion, best: AppliedPromotion | null): boolean => {
  if (best === null) return true;
  if (candidate.discount_minor !== best.discount_minor) return candidate.discount_minor > best.discount_minor;
  if (candidate.pending_conditions.length !== best.pending_conditions.length) {
    return candidate.pending_conditions.length < best.pending_conditions.length;
  }
  return candidate.promotion_id < best.promotion_id;
};

/** La plus avantageuse des promotions applicables (cf. `isBetterPromotion`) ; null : aucune. */
export function pickBestPromotion(evaluations: readonly PromotionEvaluation[]): AppliedPromotion | null {
  return evaluations.reduce<AppliedPromotion | null>(
    (best, evaluation): AppliedPromotion | null =>
      evaluation.eligible && isBetterPromotion(evaluation, best) ? evaluation : best,
    null,
  );
}

/** Remise affichée d'une promotion : « −20 % », « −10,00 € » (signe moins typographique, locale du pro). */
export function formatPromotionValueLabel(
  rules: Pick<PromotionRules, 'kind' | 'value'>,
  format: { currency: string; locale: string },
): string {
  const amount =
    rules.kind === ProPromoCodeKind.PERCENT
      ? new Intl.NumberFormat(format.locale, { style: 'percent', maximumFractionDigits: 2 }).format(rules.value / 100)
      : formatPriceFromMinorUnit(rules.value, format.currency, format.locale);
  return `\u2212${amount}`;
}

export interface BookingPromotionChoice {
  /** Promotion retenue, null : aucune. */
  applied: AppliedPromotion | null;
  /** Code saisi non retenu : sa raison (BETTER_OFFER_APPLIED si une automatique donne plus). */
  code_rejection: BookingPromoIneligibilityReason | null;
  /** Évaluation de chaque promotion, automatiques puis code. */
  evaluations: PromotionEvaluation[];
}

export interface BookingPromotionChoiceOptions {
  /**
   * Réservation ferme (pré-réservation, pose d'un code) : une condition encore en attente ne se tranchera
   * plus, elle vaut refus (`settlePromotionEvaluation`). Sans l'option (récap), elle reste annoncée.
   */
  firm?: boolean;
}

/**
 * Une seule promotion par réservation : la plus avantageuse des automatiques applicables et du code saisi.
 * Le code ne l'emporte que s'il donne au moins autant que la meilleure automatique ; sinon il est écarté
 * avec BETTER_OFFER_APPLIED (le client garde son code saisi : si l'automatique tombe plus tard, faute de
 * remplir sa condition, le code revient).
 */
export function chooseBookingPromotion(
  autoRules: readonly PromotionRules[],
  codeRules: PromotionRules | null,
  context: PromotionContext,
  options: BookingPromotionChoiceOptions = {},
): BookingPromotionChoice {
  const evaluate = (rules: PromotionRules): PromotionEvaluation =>
    options.firm ? settlePromotionEvaluation(evaluatePromotion(rules, context)) : evaluatePromotion(rules, context);
  const autoEvaluations = autoRules.map(evaluate);
  const bestAuto = pickBestPromotion(autoEvaluations);
  if (codeRules === null) return { applied: bestAuto, code_rejection: null, evaluations: autoEvaluations };

  const codeEvaluation = evaluate(codeRules);
  const evaluations = [...autoEvaluations, codeEvaluation];
  if (!codeEvaluation.eligible) return { applied: bestAuto, code_rejection: codeEvaluation.reason, evaluations };
  if (bestAuto !== null && bestAuto.discount_minor > codeEvaluation.discount_minor) {
    return { applied: bestAuto, code_rejection: BookingPromoIneligibilityReason.BETTER_OFFER_APPLIED, evaluations };
  }
  return { applied: codeEvaluation, code_rejection: null, evaluations };
}

/**
 * Conditions d'une promotion qu'une fiche ou une carte du tunnel ne peut pas garantir : happy hours,
 * lieux ou membres limités, clients ciblés. Sans elles (et montant minimum atteint), la remise est acquise et le
 * prix barré peut s'afficher ; avec elles, seulement un badge avec mention.
 */
export function listPromotionConditions(
  rules: Pick<PromotionRules, 'days_of_week' | 'time_windows' | 'place_ids' | 'team_member_ids' | 'customer_scope'>,
): PromotionPendingCondition[] {
  const conditions: PromotionPendingCondition[] = [];
  if (hasPromoHappyHours(rules)) conditions.push(PromotionPendingCondition.HAPPY_HOURS);
  if (restrictsPlaces(rules)) conditions.push(PromotionPendingCondition.PLACE);
  if (restrictsMembers(rules)) conditions.push(PromotionPendingCondition.MEMBER);
  if (rules.customer_scope !== ProPromoCodeCustomerScope.ALL) conditions.push(PromotionPendingCondition.CUSTOMER);
  return conditions;
}
